<template>
  <section class="image-resources" :class="{ 'image-resources--compact': compact, 'image-resources--embedded': paletteEmbedded }" aria-label="图片资源库">
    <p v-if="!canQuery" class="image-resources__feedback" role="status">当前账号没有图片查看权限，请联系管理员授权。</p>
    <template v-else>
      <header v-if="!paletteEmbedded" class="image-resources__header">
        <h1 v-if="!compact">图片</h1><h2 v-else>图片资源</h2>
        <div class="image-resources__actions"><form class="image-resources__search" @submit.prevent="searchImages">
          <el-input v-model="keyword" size="small" placeholder="请输入图片名称" aria-label="搜索图片名称" maxlength="128" clearable @clear="searchImages"></el-input>
          <el-button size="small" native-type="submit" icon="el-icon-search" aria-label="搜索图片"></el-button>
        </form><el-button v-if="canManage" size="small" type="primary" icon="el-icon-plus" :disabled="catalogLoading || !!catalogError || !!editLoadingId" @click="openCreate">新增图片</el-button></div>
      </header>
      <resource-catalog-palette v-if="accordionMode" :categories="categoryOptions" label="图片" :keyword="appliedKeyword" :list-key="paletteListKey" :loading="catalogLoading" :error="catalogError" @retry="loadCatalogs">
        <template slot-scope="section"><image-resource-browser :key="section.catalogId + ':' + section.listKey" compact palette-embedded :palette-catalog-id="section.catalogId" :palette-keyword="appliedKeyword" @use="$emit('use', $event)" @clear-filters="clearFilters" /></template>
      </resource-catalog-palette>
      <div v-else class="image-resources__layout">
        <aside v-if="!paletteEmbedded" class="image-resources__categories" aria-label="图片分类">
          <p v-if="catalogLoading" role="status">分类加载中…</p>
          <div v-else-if="catalogError" class="image-resources__feedback" role="alert">
            <p>{{ catalogError }}</p><el-button size="mini" @click="loadCatalogs">重试分类</el-button>
          </div>
          <template v-else>
            <el-select v-if="compact" :value="catalogId" size="small" aria-label="选择图片分类" @change="selectCategory">
              <el-option label="全部图片" value=""></el-option>
              <el-option v-for="entry in categoryOptions" :key="entry.id" :label="entry.label" :value="entry.id"></el-option>
            </el-select>
            <template v-else>
              <div class="image-resources__category-heading"><span>共 {{ categoryOptions.length }} 个分类</span><span class="image-resources__category-actions">
                <button type="button" title="搜索分类" aria-label="搜索图片分类" :aria-expanded="String(categorySearchVisible)" aria-controls="image-category-search" @click="toggleCategorySearch"><i class="el-icon-search" aria-hidden="true"></i></button>
                <button v-if="canManage" type="button" title="新增分类" aria-label="新增图片分类" @click="openCategoryCreate"><i class="el-icon-plus" aria-hidden="true"></i></button>
              </span></div>
              <el-input v-if="categorySearchVisible" id="image-category-search" ref="categorySearch" class="image-resources__category-search" v-model="categoryKeyword" size="small" placeholder="搜索分类" aria-label="搜索图片分类名称" clearable></el-input>
              <el-tree ref="tree" :data="[{ id: '', name: '全部图片', children: [] }, ...catalogs]" node-key="id" :props="{ label: 'name', children: 'children' }" :filter-node-method="filterCategory" default-expand-all highlight-current :expand-on-click-node="false" :current-node-key="catalogId" @node-click="selectCategory($event.id)"></el-tree>
            </template>
          </template>
        </aside>
        <div class="image-resources__main">
          <div ref="results" class="image-resources__results" :aria-busy="String(loading)" tabindex="0" aria-label="图片列表，可滚动浏览">
            <div v-if="loading" class="image-resources__feedback" role="status"><span class="ui-loading-spinner" aria-hidden="true"></span>图片加载中…</div>
            <div v-else-if="error" class="image-resources__feedback" role="alert">
              <p>{{ error }}</p><el-button size="small" @click="loadImages">重新加载</el-button>
            </div>
            <div v-else-if="!items.length" class="image-resources__feedback" role="status">
              <i class="el-icon-picture-outline image-resources__empty-icon" aria-hidden="true"></i>
              <p>{{ appliedKeyword || catalogId ? '没有匹配的图片' : '暂无图片资源' }}</p>
              <p v-if="!appliedKeyword && !catalogId" class="image-resources__empty-hint">上传背景、边框或装饰素材，供大屏和2D组态使用。</p>
              <el-button v-if="appliedKeyword || (catalogId && !paletteEmbedded)" type="text" @click="clearFilters">清除筛选</el-button>
              <el-button v-else-if="canManage" size="small" type="primary" :disabled="catalogLoading || !!catalogError" @click="openCreate">新增图片</el-button>
            </div>
            <div v-else class="image-resources__grid">
              <article v-for="image in items" :ref="'image-card-' + image.id" :key="image.id + ':' + image.version" class="image-resources__card">
                <div class="image-resources__thumb" :draggable="compact && image.available && !failed[image.id]" :title="compact ? '拖入画布，或双击添加' : image.name" @dragstart="startDrag($event, image)" @dragend="stopDrag" @dblclick="useImage(image)">
                  <img v-if="image.available && !failed[image.id]" :src="image.url" :alt="image.name" loading="lazy" draggable="false" @error="$set(failed, image.id, true)">
                  <span v-else class="image-resources__broken">{{ failed[image.id] ? '图片加载失败' : image.unavailableReason }}<button v-if="failed[image.id]" type="button" @click.stop="$delete(failed, image.id)" @dblclick.stop>重试</button></span>
                  <button v-if="image.available && !failed[image.id]" type="button" class="image-resources__preview" :aria-label="'预览 ' + image.name" @click.stop="openPreview(image)" @dblclick.stop><i class="el-icon-view" aria-hidden="true"></i>预览</button>
                </div>
                <footer class="image-resources__card-footer">
                  <span :title="image.name">{{ image.name }}</span>
                  <small v-if="!compact" class="image-resources__visibility">{{ image.visibility === 'public' ? '公开' : '私有' }}</small>
                  <button v-if="compact" type="button" :disabled="!image.available || !!failed[image.id]" :aria-label="'添加 ' + image.name + ' 到画布'" title="添加到画布" @click="useImage(image)"><i class="el-icon-plus" aria-hidden="true"></i></button>
                  <button v-else-if="canManage && image.editable" type="button" :disabled="!!editLoadingId || catalogLoading || !!catalogError || image.visibility === 'public' && !canPublish" :aria-label="'编辑 ' + image.name" :title="image.visibility === 'public' && !canPublish ? '编辑公开图片需要公开权限' : '编辑图片'" @click="openEdit(image)"><i :class="editLoadingId === image.id ? 'el-icon-loading' : 'el-icon-edit'" aria-hidden="true"></i></button>
                  <el-dropdown v-if="image.editable && (canManage || canDelete)" trigger="click" @command="handleImageCommand(image, $event)">
                    <button type="button" :disabled="!!editLoadingId || createVisible || restoreVisible || deleteVisible" :aria-label="'更多操作：' + image.name" title="更多操作"><i class="el-icon-more" aria-hidden="true"></i></button>
                    <el-dropdown-menu slot="dropdown" role="menu"><el-dropdown-item v-if="canManage" command="restore" icon="el-icon-refresh-left" role="menuitem" :disabled="image.version <= 1 || image.visibility === 'public' && !canPublish" :title="image.visibility === 'public' && !canPublish ? '恢复公开图片需要公开权限' : image.version <= 1 ? '这张图片尚无上一版' : '恢复上一版并保存为新版本'">恢复上一版</el-dropdown-item><el-dropdown-item v-if="canDelete" command="delete" icon="el-icon-delete" role="menuitem" class="image-resource-delete-menu-item">删除</el-dropdown-item></el-dropdown-menu>
                  </el-dropdown>
                </footer>
              </article>
            </div>
          </div>
          <footer class="image-resources__pagination" aria-label="图片分页">
            <span v-if="!compact" aria-live="polite">共 {{ total }} 张</span>
            <el-pagination :small="compact" :current-page="pageIndex + 1" :page-size="pageSize" :page-sizes="[12, 24, 48]" :total="total" :disabled="loading || !!error" :layout="compact ? 'prev, slot, next' : 'prev, pager, next, sizes'" @current-change="changePage" @size-change="changePageSize"><span v-if="compact">{{ total ? pageIndex + 1 : 0 }} / {{ Math.ceil(total / pageSize) }}</span></el-pagination>
          </footer>
        </div>
      </div>
      <resource-image-preview v-if="preview" :visible="previewVisible" :src="preview.url" :name="preview.name" @close="closePreview" />
      <image-resource-create v-if="canManage" :visible.sync="createVisible" :image="editingImage" :categories="categoryOptions" :selected-catalog="catalogId" @created="onImageCreated" @updated="onImageUpdated" @refresh="refreshImages" @update:visible="onDialogVisibility"></image-resource-create>
      <image-resource-restore v-if="canManage && restoring" :visible.sync="restoreVisible" :image-id="restoring.id" @restored="onImageRestored" @check="loadImages" @closed="returnRestoreFocus"></image-resource-restore>
      <image-resource-delete v-if="canDelete && deleting" :visible.sync="deleteVisible" :image-id="deleting.id" @deleted="onImageDeleted" @check="loadImages" @closed="returnDeleteFocus"></image-resource-delete>
      <el-dialog v-if="canManage" title="新增图片分类" :visible.sync="categoryCreateVisible" width="480px" append-to-body custom-class="management-dialog image-resource-catalog-dialog"
                 :close-on-click-modal="false" :close-on-press-escape="!categorySaving" :show-close="!categorySaving" @opened="$refs.categoryName && $refs.categoryName.focus()">
        <el-form label-position="top" @submit.native.prevent="saveCategory">
          <p v-if="categorySaveError" class="image-resource-form-error" role="alert">{{ categorySaveError }}</p>
          <el-form-item label="分类名称" required :error="categoryNameError">
            <el-input ref="categoryName" v-model="categoryForm.name" maxlength="128" :disabled="categorySaving" placeholder="请输入分类名称" aria-label="分类名称"></el-input>
          </el-form-item>
          <el-form-item label="上级分类">
            <el-select v-model="categoryForm.parentId" filterable :disabled="categorySaving" aria-label="上级图片分类" popper-class="image-resource-category-options">
              <el-option value="" label="无（一级分类）"></el-option>
              <el-option v-for="entry in categoryOptions" :key="entry.id" :value="entry.id" :label="entry.label"></el-option>
            </el-select>
          </el-form-item>
        </el-form>
        <span slot="footer"><el-button :disabled="categorySaving" @click="categoryCreateVisible = false">取消</el-button>
          <el-button v-if="categorySaveUncertain" type="primary" @click="refreshCreatedCatalogs">关闭并刷新分类</el-button>
          <el-button v-else type="primary" :loading="categorySaving" @click="saveCategory">保存分类</el-button></span>
      </el-dialog>
    </template>
  </section>
</template>

<script>
import { getImageCatalogs, queryImages, createImageCatalog, getImage } from '@/api/image-resources'
import { createRequestCancellation, isRequestCanceled } from '@/api/http'
import { imageCatalogs, imagePage, imageError, createdImageCatalog, editableImage } from '@/utils/image-resources'
import { beginImageDrag, endImageDrag, RESOURCE_IMAGE_DRAG_TYPE } from '@/editor/core/resource-image'
import ImageResourceCreate from './create-dialog.vue'
import ImageResourceRestore from './restore-dialog.vue'
import ImageResourceDelete from './delete-dialog.vue'
import ResourceImagePreview from '@/components/resource-image-preview.vue'
import ResourceCatalogPalette from '@/components/ResourceCatalogPalette.vue'
import store from '@/store'

export default {
  name: 'ImageResourceBrowser',
  components: { ImageResourceCreate, ImageResourceRestore, ImageResourceDelete, ResourceImagePreview, ResourceCatalogPalette },
  props: { compact: Boolean, grouped: Boolean, paletteEmbedded: Boolean, paletteCatalogId: { type: String, default: '' }, paletteKeyword: { type: String, default: '' } },
  data: () => ({
    catalogs: [], catalogLoading: false, catalogError: '', catalogId: '', paletteRevision: 0,
    keyword: '', appliedKeyword: '', pageIndex: 0, pageSize: 12, total: 0,
    items: [], failed: {}, loading: false, error: '',
    previewVisible: false, preview: null, createVisible: false, editingImage: null, editLoadingId: '',
    restoring: null, restoreVisible: false,
    deleting: null, deleteVisible: false,
    categorySearchVisible: false, categoryKeyword: '', categoryCreateVisible: false,
    categoryForm: { name: '', parentId: '' }, categorySaving: false, categorySaveError: '', categoryNameError: '', categorySaveUncertain: false
  }),
  computed: {
    accordionMode () { return this.compact && this.grouped && !this.paletteEmbedded },
    paletteListKey () { return JSON.stringify([this.appliedKeyword, this.paletteRevision]) },
    canQuery () { return store.getters['auth/hasResourcePermission']('visualizationImage:query') },
    canManage () { return !this.compact && this.canQuery && store.getters['auth/hasResourcePermission']('visualizationImage:save') },
    canPublish () { return this.canManage && store.getters['auth/hasResourcePermission']('visualizationImage:publish') },
    canDelete () { return !this.compact && this.canQuery && store.getters['auth/hasResourcePermission']('visualizationImage:delete') },
    categoryOptions () {
      const result = []
      const visit = (nodes, prefix = '') => nodes.forEach(node => {
        const label = prefix ? prefix + ' / ' + node.name : node.name
        result.push({ id: node.id, label })
        visit(node.children, label)
      })
      visit(this.catalogs)
      return result
    }
  },
  watch: {
    canManage (allowed) { if (!allowed) { this.restoreVisible = false; this.restoring = null; this.createVisible = false; this.editingImage = null; this.categoryCreateVisible = false } },
    canDelete (allowed) { if (!allowed) { this.deleteVisible = false; this.deleting = null } },
    categoryKeyword (value) { if (this.$refs.tree) this.$refs.tree.filter(value.trim()) },
    canQuery (allowed) {
      if (allowed) { this.loadCatalogs(); this.loadImages() }
      else {
        this._listRequest++; this._catalogRequest++; this._editRequest++
        if (this._listCancel) this._listCancel.cancel()
        if (this._catalogCancel) this._catalogCancel.cancel()
        if (this._editCancel) this._editCancel.cancel()
        this.catalogs = []; this.items = []; this.total = 0
        this.catalogLoading = false; this.loading = false; this.editLoadingId = ''
        this.createVisible = false; this.categoryCreateVisible = false; this.previewVisible = false
        this.preview = null; this.editingImage = null
        endImageDrag()
      }
    }
  },
  created () {
    this._editRequest = 0; this._listRequest = 0; this._catalogRequest = 0
    if (this.paletteEmbedded) { this.catalogId = this.paletteCatalogId; this.keyword = this.appliedKeyword = this.paletteKeyword }
    this.loadCatalogs(); if (!this.accordionMode) this.loadImages()
  },
  beforeDestroy () {
    this._listRequest++; this._catalogRequest++
    this._editRequest++
    if (this._editCancel) this._editCancel.cancel()
    if (this._listCancel) this._listCancel.cancel()
    if (this._catalogCancel) this._catalogCancel.cancel()
    endImageDrag()
  },
  methods: {
    toggleCategorySearch () {
      this.categorySearchVisible = !this.categorySearchVisible
      if (!this.categorySearchVisible) this.categoryKeyword = ''
      else this.$nextTick(() => { if (this.$refs.categorySearch) this.$refs.categorySearch.focus() })
    },
    filterCategory (keyword, data) { return !keyword || data.id === '' || data.name.toLowerCase().includes(keyword.toLowerCase()) },
    openCategoryCreate () {
      if (!this.canManage) return
      this.categoryForm = { name: '', parentId: this.catalogId }
      this.categorySaveError = ''; this.categoryNameError = ''; this.categorySaveUncertain = false; this.categoryCreateVisible = true
    },
    async saveCategory () {
      if (!this.canManage || this.categorySaving || this.categorySaveUncertain) return
      this.categoryNameError = this.categoryForm.name.trim() ? '' : '请输入分类名称'
      if (this.categoryNameError) return
      this.categorySaving = true; this.categorySaveError = ''
      try {
        const response = await createImageCatalog({ name: this.categoryForm.name.trim(), parentId: this.categoryForm.parentId || null })
        this.categorySaveUncertain = true
        const catalog = createdImageCatalog(response)
        if (this._isDestroyed) return
        this.categorySaveUncertain = false; this.categoryCreateVisible = false; this.categoryKeyword = ''
        await this.loadCatalogs()
        if (this._isDestroyed) return
        if (!this.catalogError) this.selectCategory(catalog.id)
        if (this.$message) this.$message.success('图片分类已新增')
      } catch (error) {
        if (!this._isDestroyed) {
          if (!error.status || error.status >= 500) this.categorySaveUncertain = true
          this.categorySaveError = this.categorySaveUncertain ? '保存结果未确认，请刷新分类检查，勿重复提交' : imageError(error)
        }
      } finally { if (!this._isDestroyed) this.categorySaving = false }
    },
    openCreate () {
      if (!this.canManage || this.editLoadingId || this.createVisible || this.restoreVisible || this.deleteVisible) return
      this.editingImage = null; this.createVisible = true
    },
    async openEdit (image) {
      if (image.visibility === 'public' && !this.canPublish) return
      if (!this.canManage || !image.editable || this.editLoadingId || this.createVisible || this.restoreVisible || this.deleteVisible) return
      const request = ++this._editRequest
      if (this._editCancel) this._editCancel.cancel()
      this._editCancel = createRequestCancellation(); this.editLoadingId = image.id
      try {
        const current = editableImage(await getImage(image.id, { cancelToken: this._editCancel.token }))
        if (current.visibility === 'public' && !this.canPublish) return
        if (request !== this._editRequest || this._isDestroyed || !this.canManage) return
        if (current.id !== image.id) throw new Error('图片编辑信息与所选资源不一致')
        await this.loadCatalogs()
        if (request !== this._editRequest || this._isDestroyed) return
        if (this.catalogError) throw new Error(this.catalogError)
        this.editingImage = current; this.createVisible = true
      } catch (error) {
        if (request === this._editRequest && !this._isDestroyed && !isRequestCanceled(error) && this.$message) this.$message.error(imageError(error))
      } finally { if (request === this._editRequest && !this._isDestroyed) this.editLoadingId = '' }
    },
    onDialogVisibility (visible) { if (!visible) this.$nextTick(() => { if (!this.createVisible) this.editingImage = null }) },
    handleImageCommand (image, command) { if (command === 'restore') this.openRestore(image); else if (command === 'delete') this.openDelete(image) },
    openDelete (image) {
      if (!this.canDelete || !image.editable || this.editLoadingId || this.createVisible || this.restoreVisible || this.deleteVisible) return
      this.deleting = image; this.deleteVisible = true
    },
    onImageDeleted () {
      this.loadImages()
      if (this.$message) this.$message.success('图片已从资源库删除，历史文件和已使用图片保留')
    },
    returnDeleteFocus () {
      const id = this.deleting && this.deleting.id
      this.deleting = null
      this.$nextTick(() => {
        if (this._isDestroyed) return
        const cards = this.$refs['image-card-' + id], card = Array.isArray(cards) ? cards[0] : cards
        const button = card && card.querySelector('button[title="更多操作"]')
        if (button && !button.disabled) button.focus()
        else if (this.$refs.results) this.$refs.results.focus()
      })
    },
    openRestore (image) {
      if (image.visibility === 'public' && !this.canPublish) return
      if (!this.canManage || !image.editable || image.version <= 1 || this.editLoadingId || this.createVisible || this.restoreVisible || this.deleteVisible) return
      this.restoring = image; this.restoreVisible = true
    },
    onImageRestored () {
      this.loadImages()
      if (this.$message) this.$message.success('图片上一版已恢复为新版本，已使用的固定版本保持不变')
    },
    returnRestoreFocus () {
      const id = this.restoring && this.restoring.id
      this.restoring = null
      this.$nextTick(() => {
        if (this._isDestroyed) return
        const cards = this.$refs['image-card-' + id], card = Array.isArray(cards) ? cards[0] : cards
        const button = card && card.querySelector('button[title="更多操作"]')
        if (button && !button.disabled) button.focus()
        else if (this.$refs.results) this.$refs.results.focus()
      })
    },
    onImageUpdated () {
      this.loadImages()
      if (this.$message) this.$message.success('图片已更新，已保存大屏继续使用原版本')
    },
    refreshImages (image) { if (image.editing) this.loadImages(); else this.refreshCreatedImages(image) },
    onImageCreated (image) {
      this.refreshCreatedImages(image)
      if (this.$message) this.$message.success('图片已保存到资源库')
    },
    refreshCreatedImages (image) {
      this.keyword = ''; this.appliedKeyword = ''; this.catalogId = image.catalogId; this.pageIndex = 0
      this.loadImages()
    },
    refreshCreatedCatalogs () {
      this.categoryCreateVisible = false; this.categoryKeyword = ''; this.loadCatalogs()
    },
    async loadCatalogs () {
      if (!this.canQuery || this.paletteEmbedded) return
      const request = ++this._catalogRequest
      if (this._catalogCancel) this._catalogCancel.cancel()
      this._catalogCancel = createRequestCancellation()
      this.catalogLoading = true; this.catalogError = ''
      try {
        const response = await getImageCatalogs({ cancelToken: this._catalogCancel.token })
        if (request === this._catalogRequest) {
          this.catalogs = imageCatalogs(response)
          this.$nextTick(() => { if (this.$refs.tree) this.$refs.tree.filter(this.categoryKeyword.trim()) })
        }
      } catch (error) {
        if (request === this._catalogRequest && !isRequestCanceled(error)) this.catalogError = imageError(error)
      } finally { if (request === this._catalogRequest) this.catalogLoading = false }
    },
    async loadImages () {
      if (!this.canQuery) return
      if (this.accordionMode) { this.paletteRevision++; return }
      const request = ++this._listRequest
      if (this._listCancel) this._listCancel.cancel()
      this._listCancel = createRequestCancellation()
      // 加载中保留总数，避免分页器因 total=0 自动回跳第一页并触发第二次请求。
      this.loading = true; this.error = ''; this.items = []; this.failed = {}
      endImageDrag()
      try {
        const response = await queryImages({ keyword: this.appliedKeyword, catalogId: this.catalogId, pageIndex: this.pageIndex, pageSize: this.pageSize }, { cancelToken: this._listCancel.token })
        if (request !== this._listRequest) return
        const page = imagePage(response)
        this.items = page.data; this.total = page.total; this.pageIndex = page.pageIndex; this.pageSize = page.pageSize
        this.$nextTick(() => { if (this.$refs.results) this.$refs.results.scrollTop = 0 })
      } catch (error) {
        if (request === this._listRequest && !isRequestCanceled(error)) { this.error = imageError(error); this.total = 0 }
      } finally { if (request === this._listRequest) this.loading = false }
    },
    searchImages () { this.appliedKeyword = this.keyword.trim(); this.pageIndex = 0; this.loadImages() },
    selectCategory (id) { this.catalogId = id; this.pageIndex = 0; this.loadImages() },
    clearFilters () { if (this.paletteEmbedded) { this.$emit('clear-filters'); return } this.keyword = ''; this.appliedKeyword = ''; this.catalogId = ''; this.pageIndex = 0; this.loadImages() },
    changePage (page) { if (!this.loading && !this.error && page - 1 !== this.pageIndex) { this.pageIndex = page - 1; this.loadImages() } },
    changePageSize (size) { if (!this.loading && !this.error && size !== this.pageSize) { this.pageSize = size; this.pageIndex = 0; this.loadImages() } },
    openPreview (image) { if (!this.canQuery || !image.available || this.failed[image.id]) return; this.preview = image; this.previewVisible = true },
    closePreview () { this.previewVisible = false; this.preview = null },
    useImage (image) { if (this.canQuery && this.compact && image.available && !this.failed[image.id]) this.$emit('use', image) },
    startDrag (event, image) {
      if (!this.canQuery || !this.compact || this.failed[image.id]) { event.preventDefault(); return }
      const key = beginImageDrag(image)
      if (!key) { event.preventDefault(); return }
      event.dataTransfer.setData(RESOURCE_IMAGE_DRAG_TYPE, key)
      event.dataTransfer.effectAllowed = 'copy'
    },
    stopDrag () { endImageDrag() }
  }
}
</script>

<style lang="scss">
.image-resources {
  --image-bg: #fff; --image-text: #1f2937; --image-muted: #667085; --image-border: #e4e7ed; --image-thumb: #f4f5f7; --image-accent: #1677ff;
  height: 100%; min-height: 0; min-width: 0; display: flex; flex-direction: column; overflow: hidden; padding: 20px; border-radius: 6px; background: var(--image-bg); color: var(--image-text);
  &, * { box-sizing: border-box; }
  button { cursor: pointer; font: inherit; }
  button:disabled { cursor: not-allowed; opacity: .45; }
  button:focus-visible, [tabindex]:focus-visible { outline: 2px solid var(--image-accent); outline-offset: -2px; }
  * { scrollbar-width: none; -ms-overflow-style: none; }
  *::-webkit-scrollbar { width: 0; height: 0; display: none; }
  &__header { flex: 0 0 auto; display: flex; align-items: center; justify-content: space-between; gap: 16px; margin-bottom: 16px; }
  &__header h1, &__header h2 { margin: 0; font-size: 20px; font-weight: 500; }
    &__actions { display: flex; align-items: center; gap: 16px; min-width: 0; }
    &__actions > .el-button { flex-shrink: 0; }
  &__search { display: flex; min-width: 0; width: 280px; }
  &__search .el-input { flex: 1; min-width: 0; }
  &__search .el-button { margin-left: 0; border-radius: 0 4px 4px 0; }
  &__layout { display: flex; flex: 1; min-width: 0; min-height: 0; overflow: hidden; }
  &__categories { flex: 0 0 210px; min-width: 0; min-height: 0; overflow: auto; padding-right: 16px; border-right: 1px solid var(--image-border); }
    &__category-heading { display: flex; align-items: center; justify-content: space-between; gap: 4px; margin-bottom: 14px; font-size: 14px; }
    &__category-actions { display: flex; flex-shrink: 0; }
    &__category-actions button { width: 30px; height: 32px; padding: 0; border: 0; border-radius: 4px; color: var(--image-muted); background: transparent; }
    &__category-actions button:hover:not(:disabled) { color: var(--image-accent); background: var(--image-thumb); }
    &__category-search { margin-bottom: 10px; }
  &__categories .el-tree { color: var(--image-text); background: transparent; }
  &__categories .el-tree-node__content { height: 36px; border-radius: 4px; }
  &__main { flex: 1; min-width: 0; min-height: 0; display: flex; flex-direction: column; padding-left: 20px; }
  &__results { flex: 1; min-height: 0; min-width: 0; overflow: auto; padding: 2px; }
  &__grid { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 18px; align-content: start; }
  &__card { min-width: 0; overflow: hidden; border: 1px solid var(--image-border); border-radius: 8px; }
  &__thumb { position: relative; height: clamp(110px, 13vw, 190px); display: flex; align-items: center; justify-content: center; background-color: var(--image-thumb); background-image: linear-gradient(45deg, #91a1b10d 25%, transparent 25%, transparent 75%, #91a1b10d 75%), linear-gradient(45deg, #91a1b10d 25%, transparent 25%, transparent 75%, #91a1b10d 75%); background-position: 0 0, 8px 8px; background-size: 16px 16px; overflow: hidden; }
  &__thumb[draggable="true"] { cursor: grab; }
  &__thumb img { display: block; width: 100%; height: 100%; object-fit: contain; }
  &__broken { padding: 16px; font-size: 12px; line-height: 1.6; color: var(--image-muted); text-align: center; }
  &__broken button { display: block; margin: 8px auto 0; padding: 4px 10px; border: 1px solid var(--image-border); border-radius: 4px; background: var(--image-bg); color: var(--image-accent); }
  &__thumb::after { content: ''; position: absolute; inset: 0; background: rgba(0,0,0,.5); opacity: 0; pointer-events: none; transition: opacity .15s; }
  &__preview { position: absolute; z-index: 1; left: 50%; top: 50%; transform: translate(-50%, -50%); min-width: 64px; min-height: 32px; display: flex; gap: 5px; justify-content: center; align-items: center; border: 0; border-radius: 4px; color: #fff; background: transparent; opacity: 0; transition: opacity .15s; }
  &__preview:hover { background: rgba(255,255,255,.15); }
  &__card:hover &__thumb::after, &__card:focus-within &__thumb::after { opacity: 1; }
  &__card:hover &__preview, &__card:focus-within &__preview { opacity: 1; }
  &__card-footer { display: flex; align-items: center; gap: 8px; height: 48px; padding: 0 12px; }
  &__visibility { flex: 0 0 auto; color: var(--image-muted); font-size: 11px; }
  &__card-footer > span:first-child { flex: 1; min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; font-size: 14px; font-weight: 500; }
  &__card-footer > span:last-child:not(:first-child) { flex: 0 0 auto; }
  &__card-footer button { width: 28px; height: 28px; border: 0; border-radius: 4px; color: var(--image-muted); background: transparent; }
  &__card-footer button:hover:not(:disabled) { color: var(--image-accent); background: var(--image-thumb); }
  &__pagination { flex: 0 0 auto; min-height: 48px; padding-top: 12px; display: flex; align-items: center; justify-content: flex-end; gap: 16px; font-size: 12px; }
  &__feedback { min-height: 180px; padding: 32px 12px; display: flex; flex-direction: column; justify-content: center; align-items: center; gap: 12px; color: var(--image-muted); text-align: center; font-size: 13px; }
  &__feedback p { margin: 0; line-height: 1.6; overflow-wrap: anywhere; }
}
.image-resources:not(.image-resources--compact) {
  border: 1px solid var(--image-border); border-radius: 8px;
  .image-resources__header h1 { margin: 0; font-size: 20px; line-height: 28px; font-weight: 600; }
  .image-resources__results > .image-resources__feedback { min-height: 100%; }
  .image-resources__empty-icon { font-size: 36px; color: #94a3b8; }
  .image-resources__empty-hint { max-width: 360px; font-size: 12px; }
  input { color: #1f2937 !important; background: #fff !important; border: 1px solid #dcdfe6 !important; border-radius: 4px; }
  .el-button { border-radius: 4px; }
  .el-button--primary { color: #fff; background: #1677ff; border-color: #1677ff; }
  .el-button--default { color: #1f2937; background: #fff; border-color: #dcdfe6; }
}
.image-resources--compact {
  --image-bg: transparent; --image-text: var(--editor-text, #d9e5ee); --image-muted: var(--editor-muted, #90a5b5); --image-border: var(--editor-edge, #304553); --image-thumb: rgba(127,145,160,.12);
  flex: 1; width: 100%; padding: 0; border-radius: 0;
  .image-resources__header { align-items: stretch; flex-direction: column; gap: 12px; margin-bottom: 10px; }
  .image-resources__header h2 { font-size: 14px; }
  .image-resources__search { width: 100%; }
  .image-resources__actions { width: 100%; }
  .image-resources__layout { flex-direction: column; }
  .image-resources__categories { flex: 0 0 auto; max-height: 140px; padding: 0 0 10px; border: 0; }
  .image-resources__categories .el-select { width: 100%; }
  .image-resources__main { padding: 0; }
  .image-resources__grid { grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 10px; }
  .image-resources__thumb { height: 82px; }
  .image-resources__card-footer { height: 32px; gap: 2px; padding: 0 5px; }
  .image-resources__card-footer > span:first-child { font-size: 11px; }
  .image-resources__pagination { justify-content: center; min-height: 40px; padding-top: 8px; }
  .image-resources__pagination .el-pagination { color: var(--image-text); }
  .image-resources__pagination .el-pagination button { background: var(--image-thumb); color: var(--image-text); }
}
.image-resource-delete-menu-item { color: #b42318; }
@media (max-width: 1200px) {
  .image-resources:not(.image-resources--compact) .image-resources__grid { grid-template-columns: repeat(3, minmax(0, 1fr)); }
  .image-resources:not(.image-resources--compact) .image-resources__categories { flex-basis: 170px; }
}
@media (max-width: 760px) {
  .image-resources:not(.image-resources--compact) { padding: 12px; }
  .image-resources:not(.image-resources--compact) .image-resources__layout { flex-direction: column; }
  .image-resources:not(.image-resources--compact) .image-resources__categories { flex: 0 0 auto; max-height: 120px; border: 0; padding-bottom: 10px; }
  .image-resources:not(.image-resources--compact) .image-resources__main { padding: 0; }
  .image-resources:not(.image-resources--compact) .image-resources__grid { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  .image-resources__header { flex-wrap: wrap; gap: 10px; }
    .image-resources__actions { flex: 1; flex-wrap: wrap; gap: 8px; }
  .image-resources__search { width: min(280px, 100%); }
  .image-resources__pagination { flex-wrap: wrap; gap: 4px; }
}
@media (hover: none) {
  .image-resources__preview { opacity: 1; background: rgba(0,0,0,.5); }
  .image-resources__category-actions button { width: 44px; height: 44px; }
  .image-resources__card-footer { height: 48px; }
  .image-resources__card-footer button { width: 44px; height: 44px; }
}
@media (prefers-reduced-motion: reduce) { .image-resources__preview { transition: none; } }
.image-resources--compact.image-resources--embedded{height:auto;flex:none;overflow:visible;padding:4px 2px;
  .image-resources__layout,.image-resources__main{display:block;flex:none;overflow:visible}
  .image-resources__results{flex:none;overflow:visible}
  .image-resources__feedback{min-height:96px}
  .image-resources__grid{grid-template-columns:repeat(3,minmax(0,1fr));gap:7px}
  .image-resources__thumb{height:64px}
}
</style>
